import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LoyaltyRewardsIcon from 'LuxKit/Icons/loyalty/LoyaltyRewardsIcon'
import { rem } from 'polished'
import styled from 'styled-components'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import { MALDIVES_REWARDS_PAY_CALCULATOR_TIER_PRICING_BY_CABIN } from 'loyalty/hooks/useMaldivesRewardsPayCalculatorPricing'
import { FlightsClassTypes } from 'constants/flight'

const DEFAULT_COLUMN_WIDTH = 168
const GUESTS_COLUMN_WIDTH = DEFAULT_COLUMN_WIDTH / 2

const Root = styled(VerticalSpacer)`
  width: 100%;
  min-width: 0;
`

const TableScroller = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding-inline: ${rem(12)};
  overflow-x: auto;
`

const TableCentered = styled.div`
  display: flex;
  justify-content: center;
  min-width: max-content;
`

const RewardsTable = styled.table`
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
`

const HeaderCell = styled.th`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  padding: ${rem(12)} ${rem(8)};
  background: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  color: ${props => props.theme.palette.neutral.default.one};

  &.highlight {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    border-bottom: none;
    color: ${props => props.theme.palette.highlight.primary.darkBackground};
  }
`

const BodyCell = styled.td`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  padding: ${rem(12)} ${rem(8)};
  vertical-align: middle;
  background: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &.highlight {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    border-bottom: none;
  }
`

const TableRow = styled.tr`
  &:last-child ${BodyCell} {
    border-bottom: 0;
  }
`

const PackageCell = styled(BodyCell)`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
`

const GuestsCell = styled(BodyCell)`
  width: ${rem(GUESTS_COLUMN_WIDTH)};
  text-align: center;
`

const Disclaimer = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-inline: ${rem(20)};
`

const PriceCell = styled(BodyCell)`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  text-align: center;
`

const PackageHeaderCell = styled(HeaderCell)`
  text-align: left;
`

const CenteredHeaderCell = styled(HeaderCell)`
  text-align: center;
`

const CenteredGuestsHeaderCell = styled(CenteredHeaderCell)`
  width: ${rem(GUESTS_COLUMN_WIDTH)};
`

function LoyaltyRewardsIntroTable() {
  const intl = useIntl()
  const formatAudCurrency = useCurrencyFormatter({ currency: 'AUD' })

  const packageColumnLabel = intl.formatMessage({
    id: 'LsogaAWZAMBbOjhV5hj0',
    defaultMessage: 'Maldives Package',
    description: 'Table header for the Maldives package column in the Société Rewards intro table',
  })
  const guestsColumnLabel = intl.formatMessage({
    id: '7qMp2gR_0UzJkQZMCEkV',
    defaultMessage: 'Guests',
    description: 'Table header for the guest count column in the Société Rewards intro table',
  })
  const rewardsColumnLabel = intl.formatMessage({
    id: 'oB6pz7BL-ozi_M3Ekmwq',
    defaultMessage: 'Société Rewards',
    description: 'Table header for the Société Rewards price column in the rewards intro table',
  })
  const payInFullColumnLabel = intl.formatMessage({
    id: 'W8AtvGB5S9eHbR3iQEU1',
    defaultMessage: 'Pay in full',
    description: 'Table header for the pay-in-full cash price column in the Société Rewards intro table',
  })
  const economyLabel = intl.formatMessage({
    id: '8_41AhuRcOgVxo92zxVp',
    defaultMessage: 'Economy',
    description: 'Economy fare row label in the Société Rewards intro table',
  })
  const economyPlusLabel = intl.formatMessage({
    id: 'AvrRazvSs4sZHx_KKwGG',
    defaultMessage: 'Economy Plus',
    description: 'Economy Plus fare row label in the Société Rewards intro table',
  })
  const economyExtraLabel = intl.formatMessage({
    id: 'Vi5EYgKeNumJNSJ-dl_Z',
    defaultMessage: 'Economy Extra',
    description: 'Economy Extra fare row label in the Société Rewards intro table',
  })
  const businessLabel = intl.formatMessage({
    id: 'Xu-Dr-t5Q5bXYkCs7_hX',
    defaultMessage: 'Business',
    description: 'Business fare row label in the Société Rewards intro table',
  })
  const packageDescription = intl.formatMessage({
    id: 'Pv0TnChS4nAHsLGAamhE',
    defaultMessage: 'Return flights & 5 night stay',
    description: 'Package description shown under each fare class in the Société Rewards intro table',
  })
  const guestsLabel = intl.formatMessage({
    id: 'UdqKgoKVeNJhu5eIproe',
    defaultMessage: '2 adults',
    description: 'Guest count shown for each fare class in the Société Rewards intro table',
  })
  const disclaimer = intl.formatMessage({
    id: 'EG0FLsz5oymTNEuWpBAw',
    defaultMessage: 'Points and prices are based on LuxPlus+ member rates, accurate at time of publish and subject to change.',
    description: 'Disclaimer below the hardcoded Société Rewards intro comparison table on the rewards-seats-maldives landing page',
  })
  const formatRewardCashLabel = (cashPP: number) => intl.formatMessage(
    {
      id: 'pLiAhtE0RMNLKop_xJ3P',
      defaultMessage: 'From {cashAmount} +',
      description: 'Société Rewards cash prefix for economy fare in the intro table',
    },
    { cashAmount: formatAudCurrency(cashPP, 'roundedDollar') },
  )

  const formatRewardPointsLabel = (ptsPP: number) => intl.formatMessage(
    {
      id: '8FDRxpM8ZEVfs26Y4xmK',
      defaultMessage: '{points} pts/person',
      description: 'Société Rewards points suffix for a fare class in the intro table',
    },
    { points: intl.formatNumber(ptsPP) },
  )

  const formatPayInFullLabel = (fullPP: number) => intl.formatMessage(
    {
      id: 'L1BqK0OxgSBzn363-9Xb',
      defaultMessage: 'From {cashAmount}/person',
      description: 'Pay-in-full cash value for economy fare in the intro table',
    },
    { cashAmount: formatAudCurrency(fullPP, 'roundedDollar') },
  )

  const economyTier = MALDIVES_REWARDS_PAY_CALCULATOR_TIER_PRICING_BY_CABIN[FlightsClassTypes.ECONOMY]
  const economyPlusTier = MALDIVES_REWARDS_PAY_CALCULATOR_TIER_PRICING_BY_CABIN[FlightsClassTypes.PREMIUM]
  const economyExtraTier = MALDIVES_REWARDS_PAY_CALCULATOR_TIER_PRICING_BY_CABIN[FlightsClassTypes.LUXROW]
  const businessTier = MALDIVES_REWARDS_PAY_CALCULATOR_TIER_PRICING_BY_CABIN[FlightsClassTypes.BUSINESS]

  const rows = [
    {
      fareClass: economyLabel,
      rewardCash: formatRewardCashLabel(economyTier.cashPP),
      rewardPoints: formatRewardPointsLabel(economyTier.ptsPP),
      payInFull: formatPayInFullLabel(economyTier.fullPP),
    },
    {
      fareClass: economyExtraLabel,
      rewardCash: formatRewardCashLabel(economyExtraTier.cashPP),
      rewardPoints: formatRewardPointsLabel(economyExtraTier.ptsPP),
      payInFull: formatPayInFullLabel(economyExtraTier.fullPP),
    },
    {
      fareClass: economyPlusLabel,
      rewardCash: formatRewardCashLabel(economyPlusTier.cashPP),
      rewardPoints: formatRewardPointsLabel(economyPlusTier.ptsPP),
      payInFull: formatPayInFullLabel(economyPlusTier.fullPP),
    },
    {
      fareClass: businessLabel,
      rewardCash: formatRewardCashLabel(businessTier.cashPP),
      rewardPoints: formatRewardPointsLabel(businessTier.ptsPP),
      payInFull: formatPayInFullLabel(businessTier.fullPP),
    },
  ]

  return <Root gap={12}>
    <TableScroller>
      <TableCentered>
        <RewardsTable>
          <thead>
            <tr>
              <PackageHeaderCell>
                <BodyText variant="small" weight="bold">{packageColumnLabel}</BodyText>
              </PackageHeaderCell>
              <CenteredGuestsHeaderCell>
                <BodyText variant="small" weight="bold">{guestsColumnLabel}</BodyText>
              </CenteredGuestsHeaderCell>
              <CenteredHeaderCell className="highlight">
                <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
                  <LoyaltyRewardsIcon size={16} colour="highlight-primary-dark" />
                  <BodyText variant="small" weight="bold">{rewardsColumnLabel}</BodyText>
                </Group>
              </CenteredHeaderCell>
              <CenteredHeaderCell>
                <BodyText variant="small" weight="bold">{payInFullColumnLabel}</BodyText>
              </CenteredHeaderCell>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <TableRow key={row.fareClass}>
                <PackageCell>
                  <BodyText variant="small" weight="bold">{row.fareClass}</BodyText>
                  <BodyText variant="small">{packageDescription}</BodyText>
                </PackageCell>
                <GuestsCell>
                  <BodyText variant="small">{guestsLabel}</BodyText>
                </GuestsCell>
                <PriceCell className="highlight">
                  <BodyText variant="small" weight="bold">{row.rewardCash}</BodyText>
                  <BodyText variant="small">{row.rewardPoints}</BodyText>
                </PriceCell>
                <PriceCell>
                  <BodyText variant="small" weight="bold">{row.payInFull}</BodyText>
                </PriceCell>
              </TableRow>
            ))}
          </tbody>
        </RewardsTable>
      </TableCentered>
    </TableScroller>
    <Disclaimer>
      <Caption variant="medium" align="center" wrap="pretty">{disclaimer}</Caption>
    </Disclaimer>
  </Root>
}

export default LoyaltyRewardsIntroTable
